import { useMutation } from '@apollo/client';
import { ExpandableGroup, PanelCell } from '@teamsnap/teamsnap-ui';
import * as React from 'react';
import * as GetForm from 'core/gql/queries/show-form';
import * as DeleteParticipantGroup from 'core/gql/mutations/delete-participant-group';
import * as UpdateParticipantGroup from 'core/gql/mutations/update-participant-groups';
import ComboBox from '@teamsnap/teamsnap-ui/dist/js/components/ComboBox/Combobox';
import { flatten } from 'lodash';

import * as Types from './types';
import { deleteParticipantGroupRequest, updateParticipantGroupRequest } from './serverRequests';
import { groupBy, sortByAlpha } from 'core/utils';
import Tags from './Tags';
import { reactiveFeedbackMessage } from 'core/apollo-cache';

type ParticipantGroupProps = {
  handleStateChange: (propertyName: string, keyValue: any) => void;
  currentState: Types.State;
  formId: number;
  orgId: number;
  setUpdatedGroups: any;
  divisionsTeams: any[];
};

const ParticipantGroups: React.FC<ParticipantGroupProps> = ({
  handleStateChange,
  currentState,
  formId,
  setUpdatedGroups,
  divisionsTeams,
  orgId,
}) => {
  const refetchForm = {
    refetchQueries: () =>
      formId && [{ query: GetForm.QUERY, variables: { formId: formId, organizationId: +orgId }, skip: !formId }],
  };

  const [groups, setGroups] = React.useState<Types.ParticipantGroup[]>([]);

  // refetch form to get its updated list of fees and update fees state after deleting groups
  const [deleteParticipantGroup] = useMutation(DeleteParticipantGroup.MUTATION, refetchForm);
  const [updateParticipantGroup] = useMutation(UpdateParticipantGroup.MUTATION, refetchForm);

  const updateGroup = (prevState, index, value, fieldName) => {
    const newState = prevState;

    newState[index] = {
      ...newState[index],
      [fieldName]: value,
    };

    // Track groups so the number of update requests required is known.
    const updatedGroup = newState[index];

    setUpdatedGroups((prevState) => {
      if (!prevState.find((g) => g.id === updatedGroup.id)) {
        return [...prevState, updatedGroup];
      } else {
        const index = prevState.indexOf(prevState.find((g) => g.id === updatedGroup.id));
        prevState.splice(index, 1, updatedGroup);
        return prevState;
      }
    });

    // Return new state.
    return [...newState];
  };

  const handleGroupNameBlur= (newGroupsState) => {
    const nameChanged = () => newGroupsState.some(g => currentState['participantGroups'].value.some(g2 => g.id === g2.id && g.name !== g2.name));

    if(nameChanged()) {
      handleGroupsStateChange(newGroupsState);
    }
  }

  const handleGroupsStateChange = (newGroupsState) => {
    const noEmptyNames = () => !newGroupsState.some((g) => g.name === '');

    if (noEmptyNames()) {
      // Update Stepper state.
      const stateObj = {
        ...currentState['participantGroups'],
        value: [...newGroupsState],
      };

      handleStateChange('participantGroups', stateObj);
    }
  };

  const handleAddGroups = (groupName: string) => {
    const formattedGroup = groupName.trim().replace(/\s\s+/g, ' ');
    // Do not allow duplicates
    const existing = groups.find((group) => group.name === formattedGroup);

    if (!existing) {
      const updateGroupList = [
        ...groups,
        {
          name: formattedGroup,
          description: '',
        },
      ];

      handleGroupsStateChange(updateGroupList);
      setGroups(updateGroupList);
    } else if (existing.active === false) {
      reactiveFeedbackMessage({
        // eslint-disable-next-line quotes
        message:
          'The registration option you are trying to create has been previously deleted, please try a different name.',
        isError: true,
        autoclose: true,
      });
    }
  };

  const handleLabelChange = React.useCallback((value, index) => {
    setGroups((prevState) => updateGroup(prevState, index, value, 'name'));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDescriptionChange = React.useCallback((event, index) => {
    setGroups((prevState) => updateGroup(prevState, index, event.target.value, 'description'));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleParticipantGroupDelete = async (group) => {
    const updatedGroupSet = groups.filter((currentGroup) => currentGroup.name !== group.name);

    handleGroupsStateChange(updatedGroupSet);

    const previousState = groups;

    setGroups(updatedGroupSet);
    setUpdatedGroups((prevState) => prevState.filter((g) => g.id !== group.id));

    if (group.id) {
      try {
        const variables = {
          formId,
          id: group.id,
        };

        const result = await deleteParticipantGroupRequest(variables, deleteParticipantGroup);

        if (result === 'ERROR') {
          setGroups(previousState);
        }
      } catch (e) {
        console.error(`registration::handleParticipantGroupDelete(): ${e}`);
      }
    }
  };

  const handleParticipantGroupArchiving = async (group) => {
    const updatedGroupSet = groups.map((currentGroup) => {
      if (currentGroup.name === group.name) {
        return {
          ...currentGroup,
          active: !currentGroup.active,
        };
      } else {
        return currentGroup;
      }
    });

    handleGroupsStateChange(updatedGroupSet);
    setGroups(updatedGroupSet);

    if (group.id) {
      try {
        const variables = {
          formId,
          participantGroups: [{ id: group.id, active: !group.active }],
        };

        await updateParticipantGroupRequest(variables, updateParticipantGroup);
      } catch (e) {
        console.error(`registration::handleParticipantGroupArchiving(): ${e}`);
      }
    }
  };

  // Fired when we leave focus after renaming a group.
  const handleGroupSort = () => {
    if (currentState.participantGroups.value) {
      let grouping = currentState.participantGroups.value;

      if (grouping.length > 0) {
        grouping = grouping.map((group, index) => {
          const participantGroupObject = {
            id: group.id,
            name: group.name,
            description: group.description,
            active: group.active !== false,
            hasFormResults: group.hasFormResults,
            expanded: false,
          };

          return participantGroupObject;
        });

        const sortedGroups = grouping.sort((a, b) => {
          const nameA = a.name.toUpperCase();
          const nameB = b.name.toUpperCase();

          if (nameA < nameB) {
            return -1;
          }

          if (nameA > nameB) {
            return 1;
          }

          // names must be equal
          return 0;
        });

        // Always open the first entry
        sortedGroups[0].expanded = true;

        setGroups(sortedGroups);
      }
    }
  };

  const buildDivisionsTeamsOptions = () => {
    const sortedList = [];
    const groups = groupBy('__typename', divisionsTeams || []);
    Object.keys(groups)
      .sort()
      .forEach((key) => {
        const values = groups[key];
        sortedList.push(values.sort((a, b) => sortByAlpha(a.name, b.name)));
      });
    return flatten(sortedList)?.map((d) => ({ value: d.name, label: d.name })) || [];
  };

  const handleDivisionTeamsAsGroups = (checkedOptions) => {
    const newGroups = [];
    const index = checkedOptions.indexOf('select-all');
    if (index !== -1) {
      checkedOptions.splice(index, 1); // remove select-all checked option
    }

    groups.forEach(async (group) => {
      const indexOnCheckedOptions = checkedOptions.indexOf(group.name);
      const indexOnDivisionsTeams = divisionsTeams.map((d) => d.name).indexOf(group.name);
      if (indexOnCheckedOptions !== -1 && indexOnDivisionsTeams !== -1) {
        newGroups.push(group); // will include if is is included in the divisions and teams and is still checked
      } else if (indexOnDivisionsTeams === -1) {
        newGroups.push(group); // will include if is not a name from divisions and teams
      } else {
        if (group.id) {
          try {
            const variables = {
              formId,
              id: group.id,
            };

            await deleteParticipantGroupRequest(variables, deleteParticipantGroup);
          } catch (e) {
            console.error(`registration::handleDivisionTeamsAsGroups() when deleting group: ${e}`);
          }
        }
      }
    });

    checkedOptions.forEach((divisionTeamName) => {
      const indexOnGroups = newGroups.map((d) => d.name).indexOf(divisionTeamName);
      if (indexOnGroups === -1) {
        newGroups.push({
          name: divisionTeamName,
          description: '',
        }); // will add if it does not exist already
      }
    });

    handleGroupsStateChange(newGroups);

    setGroups(newGroups);
  };

  const getSelectedDivisionsTeams = () => {
    const selected = groups?.map((g) => g.name);
    const checker = (arr, target) => target?.every((v) => arr.includes(v));
    const isAllSelected = checker(
      groups?.map((g) => g.name),
      divisionsTeams?.map((g) => g.name)
    );
    if (isAllSelected) {
      selected.push('select-all');
    }
    return selected;
  };

  const handleOnExpandedChange = (groupId: number) => {
    // Find the index of the targeted row
    const targetIndex = groups.findIndex((group) => group.id === groupId);
    // Make a copy since we should not try to change a passed param
    const groupsCopy = [...groups];
    // Toggle the expanded state of the target row
    groupsCopy[targetIndex].expanded = !groups[targetIndex].expanded;
    // Update parent state
    setGroups(groupsCopy);
  };

  const ExpandableGroups = groups.map((group, index) => (
    <ExpandableGroup
      deleteDisabled={group.hasFormResults}
      deleteTooltipText={
        group.hasFormResults
          ? 'Registration options that have registrants cannot be deleted.'
          : 'Once deleted, this registration option cannot be restored.'
      }
      toggleTooltipText="If turned off, this registration option will be hidden from future registrants. Past registrants won't be affected. You can turn the registration option back on."
      onToggle={() => handleParticipantGroupArchiving(group)}
      toggleValue={group.active}
      isExpanded={group?.expanded}
      key={`${group.id}_${index}`}
      label={group.name}
      onLabelChange={(value) => handleLabelChange(value, index)}
      onLabelBlur={() => handleGroupNameBlur(groups)}
      onDelete={() => handleParticipantGroupDelete(group)}
      onExpandedChange={() => handleOnExpandedChange(group.id)}
      testId={group.name}
    >
      <PanelCell testId={`participant-group-${group.id}_${index}`} className="Panel-cell u-padTopNone u-spaceSidesLg">
        <p className="u-fontSizeLg u-spaceBottomXs">Option Description</p>

        <textarea
          style={{ height: 'auto', resize: 'none' }}
          className="Input u-colorNeutral8"
          value={group.description || ''}
          name="locationNotesInput"
          id="locationNotesInput"
          cols={30}
          rows={3}
          onChange={(event) => handleDescriptionChange(event, index)}
          onBlur={() => handleGroupsStateChange(groups)}
          placeholder="e.g. For boys entering 5th grade in the Fall"
        />

        <span className="u-fontSizeSm u-colorNeutral8">
          Provide relevant information to help registrants make the correct selection.
        </span>
      </PanelCell>
    </ExpandableGroup>
  ));

  // Load our data. participantGroupObject will be used to define the shape of our update payload.
  React.useEffect(() => {
    handleGroupSort();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentState.participantGroups.value]);

  return (
    <div className="participant-groups--wrapper">
      <div className="participant-groups--input_and_helper">
        <div className="u-flex u-flexRow">
          <div className="u-size7of12">
            <Tags
              placeholder="e.g. 8U Boys"
              onChange={(groupName) => handleAddGroups(groupName)}
              subLabel='Create a new registration option by entering the name and pressing or clicking "Enter."'
            />
          </div>

          <div className="u-size1of12 u-flex u-flexJustifyCenter u-spaceTopMd">
            <p>Or</p>
          </div>

          <div
            style={{ height: 56 }}
            className="u-size4of12 u-flex u-flexJustifyCenter u-padTopSm u-bgPrimary2 u-borderRadiusMd"
          >
            <ComboBox
              testId="participant-groups-combobox"
              mods="cursor-pointer u-flexAlignSelfCenter"
              selected={getSelectedDivisionsTeams()}
              onChange={(names) => handleDivisionTeamsAsGroups(names)}
              name="seasonDivisionsTeams"
              buttonLabel="Add existing divisions or teams"
              items={buildDivisionsTeamsOptions()}
              showFromHyperlink
              showSelectAll
              canCancel={true}
              doneButtonText="Copy"
              searchPlaceholder="Search for a name"
            />
          </div>
        </div>
      </div>

      <div className="participant-groups--grouping">{ExpandableGroups}</div>
    </div>
  );
};

export default ParticipantGroups;
